import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Robot,
  ChatCircle,
  UserPlus,
  TrendUp,
  ArrowLeft,
  Download,
  ArrowsClockwise,
  CalendarBlank,
  ChartBar,
  Tag,
  Question,
  Stack,
  Users,
  CaretRight,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Chart component for usage trend
const AskStewieChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400">
        No data available for chart
      </div>
    );
  }

  const maxDau = Math.max(...data.map(d => d.dau_ask || 0), 1);
  const maxQuestions = Math.max(...data.map(d => d.questions_count || 0), 1);
  
  const width = 100;
  const height = 200;
  const padding = { top: 20, right: 10, bottom: 30, left: 10 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const getX = (index) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const getYDau = (value) => padding.top + chartHeight - (value / maxDau) * chartHeight;
  const getYQuestions = (value) => padding.top + chartHeight - (value / maxQuestions) * chartHeight;

  const dauPoints = data.map((d, i) => `${getX(i)},${getYDau(d.dau_ask || 0)}`).join(' ');
  const questionsPoints = data.map((d, i) => `${getX(i)},${getYQuestions(d.questions_count || 0)}`).join(' ');

  return (
    <div className="relative w-full h-64">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + chartHeight * ratio}
            x2={width - padding.right}
            y2={padding.top + chartHeight * ratio}
            stroke="#e2e8f0"
            strokeWidth="0.5"
          />
        ))}
        <polyline points={dauPoints} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={questionsPoints} fill="none" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4,2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => (
          <circle key={i} cx={getX(i)} cy={getYDau(d.dau_ask || 0)} r="3" fill="#8b5cf6" />
        ))}
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-violet-500 rounded"></div>
          <span className="text-slate-600">DAU (Users Asking)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-emerald-500 rounded" style={{background: 'repeating-linear-gradient(90deg, #10b981 0, #10b981 4px, transparent 4px, transparent 6px)'}}></div>
          <span className="text-slate-600">Questions</span>
        </div>
      </div>
    </div>
  );
};

// Cluster trend mini chart
const ClusterTrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.question_count || 0);
  const max = Math.max(...values, 1);
  const width = 200;
  const height = 60;
  const padding = 4;
  
  const points = values.map((val, i) => {
    const x = padding + (i / (values.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - (val / max) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {values.map((val, i) => (
        <circle
          key={i}
          cx={padding + (i / (values.length - 1 || 1)) * (width - padding * 2)}
          cy={height - padding - (val / max) * (height - padding * 2)}
          r="3"
          fill="#8b5cf6"
        />
      ))}
    </svg>
  );
};

export const AskStewieAnalyticsPage = () => {
  const { department = 'substation' } = useParams();
  const { api, isSupervisorPlus, hasRole } = useAuth();
  const navigate = useNavigate();
  
  const [rollups, setRollups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [clustersSummary, setClustersSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterDetail, setClusterDetail] = useState(null);
  const [loadingCluster, setLoadingCluster] = useState(false);
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const isAdmin = hasRole && hasRole('ADMIN');

  const fetchData = useCallback(async () => {
    if (!isSupervisorPlus()) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [rollupsRes, todayRes, clustersRes] = await Promise.all([
        api.get(`/adoption/${department}/askstewie?start=${startDate}&end=${endDate}`),
        api.get(`/adoption/${department}/askstewie/today`),
        api.get(`/adoption/${department}/askstewie/clusters?start=${startDate}&end=${endDate}&limit=20`)
      ]);
      
      setRollups(rollupsRes.data?.rollups || []);
      setSummary(rollupsRes.data?.summary || null);
      setTodayData(todayRes.data);
      setClusters(clustersRes.data?.clusters || []);
      setClustersSummary({ total: clustersRes.data?.total_clusters || 0 });
    } catch (error) {
      console.error('Failed to fetch AskStewie adoption data:', error);
      toast.error('Failed to load AskStewie adoption data');
    } finally {
      setLoading(false);
    }
  }, [api, department, startDate, endDate, isSupervisorPlus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/adoption/${department}/askstewie/export?start=${startDate}&end=${endDate}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `askstewie_adoption_${department}_${startDate}_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleRunRollup = async () => {
    try {
      await api.post(`/adoption/${department}/askstewie/run-rollup`);
      toast.success('AskStewie rollup completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rollup failed');
    }
  };

  const openClusterDetail = async (cluster) => {
    setSelectedCluster(cluster);
    setLoadingCluster(true);
    try {
      const res = await api.get(`/adoption/${department}/askstewie/clusters/${cluster.cluster_id}`);
      setClusterDetail(res.data);
    } catch (error) {
      toast.error('Failed to load cluster details');
    } finally {
      setLoadingCluster(false);
    }
  };

  if (!isSupervisorPlus()) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Supervisor access required to view AskStewie analytics.</p>
      </div>
    );
  }

  const chartData = [...rollups].reverse();

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="askstewie-analytics-page">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 -ml-2">
          <ArrowLeft size={18} className="mr-2" />
          Back to Hub
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Robot size={28} weight="fill" className="text-violet-600" />
              AskStewie Adoption Analytics
            </h1>
            <p className="text-slate-600 mt-1">Track AI assistant engagement, clusters, and popular topics</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRunRollup}>
              <ArrowsClockwise size={16} className="mr-2" />
              Run Rollup
            </Button>
            <Button onClick={handleExport} disabled={exporting}>
              <Download size={16} className="mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </div>

      {/* Today's Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-violet-500 rounded-lg flex items-center justify-center">
                <ChatCircle size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-violet-600 font-medium">Today's DAU</p>
                <p className="text-3xl font-bold text-violet-900">{loading ? '-' : todayData?.dau_ask || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Question size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Questions Today</p>
                <p className="text-3xl font-bold text-emerald-900">{loading ? '-' : todayData?.questions_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <TrendUp size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Users</p>
                <p className="text-3xl font-bold text-purple-900">{loading ? '-' : todayData?.total_ask_users_to_date || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <ChartBar size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Avg DAU</p>
                <p className="text-3xl font-bold text-amber-900">{loading ? '-' : summary?.avg_dau_ask || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                <Stack size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-indigo-600 font-medium">Question Clusters</p>
                <p className="text-3xl font-bold text-indigo-900">{loading ? '-' : clustersSummary?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Question Clusters */}
      <Card className="mb-6 border-2 border-violet-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stack size={20} className="text-violet-600" />
            Top Question Clusters (Most Common Asked)
          </CardTitle>
          <CardDescription>
            Similar questions are automatically grouped together. Click a cluster to view details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : clusters.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Stack size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No question clusters found for this date range</p>
              <p className="text-sm mt-1">Clusters are created as users ask questions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clusters.slice(0, 10).map((cluster, index) => (
                <div
                  key={cluster.cluster_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-violet-50 cursor-pointer transition-colors border border-transparent hover:border-violet-200"
                  onClick={() => openClusterDetail(cluster)}
                  data-testid={`cluster-row-${index}`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="bg-violet-100 text-violet-700 text-xs">
                        #{index + 1}
                      </Badge>
                      <span className="font-medium text-slate-900 truncate">
                        {cluster.canonical_question?.substring(0, 80)}{cluster.canonical_question?.length > 80 ? '...' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Question size={14} />
                        {cluster.total_count || cluster.count || 0} questions
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {cluster.unique_users || 0} users
                      </span>
                      {cluster.topic_tags && cluster.topic_tags.length > 0 && (
                        <div className="flex gap-1">
                          {cluster.topic_tags.slice(0, 3).map((t, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-slate-100">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <CaretRight size={20} className="text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Topics */}
      {summary?.top_topics && summary.top_topics.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag size={20} className="text-violet-600" />
              Top Topics (Selected Range)
            </CardTitle>
            <CardDescription>
              Most frequently asked about topics across {summary.days_in_range || 0} days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {summary.top_topics.map((t, i) => (
                <Badge 
                  key={i} 
                  variant="outline" 
                  className={`text-sm py-1 px-3 ${
                    i === 0 ? 'bg-violet-100 text-violet-800 border-violet-300' :
                    i === 1 ? 'bg-purple-100 text-purple-800 border-purple-300' :
                    i === 2 ? 'bg-indigo-100 text-indigo-800 border-indigo-300' :
                    'bg-slate-100 text-slate-700 border-slate-300'
                  }`}
                >
                  {t.topic}: {t.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Start Date</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="start-date-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">End Date</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="end-date-input" />
            </div>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <ArrowsClockwise size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Robot size={20} className="text-violet-600" />
            AskStewie Usage Trend
          </CardTitle>
          <CardDescription>
            Daily active users and questions asked ({rollups.length} days)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <AskStewieChart data={chartData} />
          )}
        </CardContent>
      </Card>

      {/* Daily Rollups Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Rollups</CardTitle>
          <CardDescription>Detailed daily AskStewie adoption metrics with top clusters</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : rollups.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Robot size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No AskStewie rollup data available for this date range</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">DAU</TableHead>
                  <TableHead className="text-right">Questions</TableHead>
                  <TableHead className="text-right">New Users</TableHead>
                  <TableHead>Top Clusters</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rollups.map((rollup, index) => (
                  <TableRow key={rollup.rollup_date} data-testid={`rollup-row-${index}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <CalendarBlank size={16} className="text-slate-400" />
                        {new Date(rollup.rollup_date).toLocaleDateString('en-US', {
                          weekday: 'short', month: 'short', day: 'numeric'
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-violet-50 text-violet-700">
                        {rollup.dau_ask || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                        {rollup.questions_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {rollup.new_ask_users > 0 ? (
                        <Badge className="bg-blue-100 text-blue-700">+{rollup.new_ask_users}</Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(rollup.top_clusters || []).slice(0, 2).map((c, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-violet-50 text-violet-700 truncate max-w-[150px]">
                            {c.canonical?.substring(0, 30)}... ({c.count})
                          </Badge>
                        ))}
                        {(!rollup.top_clusters || rollup.top_clusters.length === 0) && (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Cluster Detail Dialog */}
      <Dialog open={!!selectedCluster} onOpenChange={() => { setSelectedCluster(null); setClusterDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stack size={20} className="text-violet-600" />
              Question Cluster Details
            </DialogTitle>
            <DialogDescription>
              View cluster statistics, trend, and example questions
            </DialogDescription>
          </DialogHeader>
          
          {loadingCluster ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : clusterDetail ? (
            <div className="space-y-6">
              {/* Canonical Question */}
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                <p className="text-sm text-violet-600 font-medium mb-1">Representative Question</p>
                <p className="text-slate-900 font-medium">{clusterDetail.cluster?.canonical_question}</p>
                {clusterDetail.cluster?.topic_tags && clusterDetail.cluster.topic_tags.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {clusterDetail.cluster.topic_tags.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-white">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-violet-600">{clusterDetail.cluster?.total_count || 0}</p>
                  <p className="text-sm text-slate-500">Total Questions</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{clusterDetail.cluster?.unique_users || 0}</p>
                  <p className="text-sm text-slate-500">Unique Users</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-600">
                    {clusterDetail.cluster?.created_at ? new Date(clusterDetail.cluster.created_at).toLocaleDateString() : '-'}
                  </p>
                  <p className="text-sm text-slate-500">First Asked</p>
                </div>
              </div>

              {/* Trend Chart */}
              {clusterDetail.trend && clusterDetail.trend.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">30-Day Trend</p>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <ClusterTrendChart data={clusterDetail.trend} />
                  </div>
                </div>
              )}

              {/* Example Questions (Admin only) */}
              {isAdmin && clusterDetail.example_questions && clusterDetail.example_questions.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Example Questions</p>
                  <div className="space-y-2">
                    {clusterDetail.example_questions.map((ex, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg border">
                        <p className="text-sm text-slate-700">{ex.q}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(ex.at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!isAdmin && (
                <p className="text-sm text-slate-500 italic">
                  Admin access required to view example questions
                </p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AskStewieAnalyticsPage;
